/**
 * Cœur de calcul de la paie — alimenté par les RUBRIQUES classifiées (référentiel paie v2).
 *
 * Catégories (onglet RUBRIQUES du référentiel) :
 *  - CAT1 : base CNSS + base CNAMGS + base TCS (+ IRPP)
 *  - CAT2 « CNSS seule » : dans le brut et la base CNSS, hors base CNAMGS et hors base TCS
 *  - CAT3 : hors brut, ajoutée au net à payer (34050, 34080, 34150…)
 *
 * Cascade (aucune autre implémentation dans le dépôt) :
 *  1. brut soumis (« Total Brut ») = CAT1 + CAT2
 *  2. CNSS / CNAMGS / TCS = `calculerCotisations` (arrondi unique ROUND, `montantCotisation`)
 *  3. base TCS = CAT1 − CNSS salarié − CNAMGS salarié (avant seuil d'exonération)
 *  4. IRPP = `calculerIRPP` (fonction unique, base TCS × 12 / parts)
 *  5. salaire net = brut soumis − CNSS − CNAMGS − TCS − IRPP
 *  6. net à payer = salaire net + CAT3 − avances (− AEN non versé, − CSS en attente de décision,
 *     appliqués par l'appelant)
 */
import { CategorieRubrique, type RubriquePaie } from './CategorieRubrique';
import { calculerCotisations, type CotisationsResult } from './Cotisations';
import { baseIrppMensuelle, calculerIRPP, type CalculIRPPResult } from './CalculIRPP';
import { EXO_GROUPES } from './PaieGabonReferentiel';
import { defaultParametresFiscauxPaie, type ParametresFiscauxPaie } from './ParametresFiscauxPaie';
import { roundFCFA } from '@shared/utils/round';

/** Contexte annuel d'un bulletin (ce qui dépend des autres bulletins du salarié). */
export interface ContextePaieRubriques {
  /**
   * Exonération art. 91 11° déjà consommée par les bulletins antérieurs de la même année civile. Le plafond de
   * 4 000 000 FCFA est ANNUEL et GLOBAL (IRPP et TCS, art. 91 11° et art. 347) : il se cumule d'un mois à l'autre.
   */
  exoArt9111DejaUtilise?: number;
}

export interface ResultatPaieRubriques {
  rubriques: readonly RubriquePaie[];
  cat1_total: number;
  cat2_total: number;
  cat3_total: number;
  /** « Total Brut » du référentiel = CAT1 + CAT2. */
  brut_soumis: number;
  /** Total versé = CAT1 + CAT2 + CAT3. */
  brut_verse: number;
  cotisations: CotisationsResult;
  cnss: number;
  cnamgs: number;
  base_tcs: number;
  tcs: number;
  /** Exonération art. 91 11° effectivement appliquée ce mois-ci à la TCS et à l'IRPP (0 si paramètre désactivé). */
  exo_art91_11: number;
  /** Cumul annuel de l'exonération art. 91 11° après ce bulletin (≤ 4 000 000). */
  exo_art91_11_cumul: number;
  irpp: CalculIRPPResult;
  irpp_mensuel: number;
  total_retenues: number;
  /** Salaire net = brut soumis − CNSS − CNAMGS − TCS − IRPP. */
  net: number;
  /** Net + CAT3 (avant avances, AEN, CSS). */
  net_avec_non_cotisable: number;
}

export function calculerPaieRubriques(
  rubriques: readonly RubriquePaie[],
  parts: number,
  params: ParametresFiscauxPaie = defaultParametresFiscauxPaie(),
  contexte: ContextePaieRubriques = {},
): ResultatPaieRubriques {
  let cat1 = 0;
  let cat2 = 0;
  let cat3 = 0;
  let art9111 = 0;
  for (const r of rubriques) {
    const m = roundFCFA(r.montant);
    if (r.categorie === CategorieRubrique.CAT1_IMPOSABLE) cat1 += m;
    else if (r.categorie === CategorieRubrique.CAT2_INDEM_BRUT) cat2 += m;
    else cat3 += m;
    if (r.exoIrppGroupe === 'art91_11' && r.categorie !== CategorieRubrique.CAT3_NON_COTISAL) {
      art9111 += m;
    }
  }

  const brut_soumis = cat1 + cat2;
  /**
   * Art. 91 11° et art. 347 : primes de performance, rendement, intéressement, gratifications, bonus, treizième mois,
   * primes de fin d'année, exonérées d'IRPP et de TCS « dans la limite annuelle et globale de 4 000 000 FCFA ».
   * Le plafond est annuel : on retire du mois ce qu'il reste après les bulletins antérieurs de l'année.
   */
  const exoDejaUtilise = Math.max(0, roundFCFA(contexte.exoArt9111DejaUtilise ?? 0));
  const resteAnnuel = Math.max(0, EXO_GROUPES.art91_11.plafond_annuel - exoDejaUtilise);
  const exo_art91_11 = params.exonerationArt9111Active ? Math.min(art9111, resteAnnuel) : 0;
  const cotisations = calculerCotisations(
    { brutTotal: brut_soumis, cat2, exoArt9111: exo_art91_11 },
    params,
  );
  const cnss = cotisations.cnss_vid_sal;
  const cnamgs = cotisations.cnamgs_sal;
  const base_tcs = cotisations.base_tcs;
  const tcs = cotisations.tcs;

  const irpp = calculerIRPP(baseIrppMensuelle(base_tcs, tcs), parts, { params });

  const total_retenues = cnss + cnamgs + tcs + irpp.irpp_mensuel;
  const net = brut_soumis - total_retenues;

  return {
    rubriques,
    cat1_total: cat1,
    cat2_total: cat2,
    cat3_total: cat3,
    brut_soumis,
    brut_verse: brut_soumis + cat3,
    cotisations,
    cnss,
    cnamgs,
    base_tcs,
    tcs,
    exo_art91_11,
    exo_art91_11_cumul: exoDejaUtilise + exo_art91_11,
    irpp,
    irpp_mensuel: irpp.irpp_mensuel,
    total_retenues,
    net,
    net_avec_non_cotisable: net + cat3,
  };
}
